import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { Auth0Wrapper, useAuth } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import Identities from './pages/Identities';
import IdentityDetail from './pages/IdentityDetail';
import Activity from './pages/Activity';
import RunDetail from './pages/RunDetail';
import ApiKeys from './pages/ApiKeys';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Pricing from './pages/Pricing';
import OrganizationSettings from './pages/OrganizationSettings';
import AcceptInvite from './pages/AcceptInvite';
import OnboardingTour from './onboarding/OnboardingTour';
import { setupAxiosInterceptor } from './utils/axiosInterceptor';
import './App.css';

// Enhanced loading component - supports light/dark mode
function LoadingPage() {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 text-gray-900 dark:text-white relative overflow-hidden transition-colors duration-300">
      {/* Animated background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-blue-600/5">
        <div className="absolute inset-0 opacity-30">
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/10 to-transparent"
            style={{
              animation: 'loadingWave 10s ease-in-out infinite',
              transform: 'translateX(-100%) rotate(45deg)'
            }}
          />
        </div>
      </div>

      <div className="relative z-10 text-center">
        {/* Enhanced loading spinner */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-spin">
            <div className="absolute inset-2 rounded-full bg-white dark:bg-gray-900 transition-colors duration-300"></div>
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-blue-400/50 to-transparent animate-ping"></div>
        </div>

        <h2 className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-200">Loading Sente</h2>
        <p className="text-gray-500 dark:text-gray-400">Initializing secure connection...</p>

        {/* Loading dots */}
        <div className="flex justify-center space-x-1 mt-4">
          <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-blue-400 dark:bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
          <div className="w-2 h-2 bg-blue-300 dark:bg-blue-200 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
        </div>
      </div>

      {/* CSS for loading animations */}
      <style>
        {`
          @keyframes loadingWave {
            0% {
              transform: translateX(-100%) rotate(45deg);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(300%) rotate(45deg);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, login } = useAuth();

  // Check if we're processing an Auth0 callback (URL has code and state params)
  const isProcessingCallback = window.location.search.includes('code=') &&
                                window.location.search.includes('state=');

  // Auto-login effect
  useEffect(() => {
    // Don't trigger login if we're processing a callback or still loading
    if (!loading && !isAuthenticated && !isProcessingCallback) {
      // Preserve current URL for redirect after login via Auth0 appState
      const currentPath = window.location.pathname + window.location.search;
      login(currentPath);
    }
  }, [loading, isAuthenticated, login, isProcessingCallback]);

  // If Auth0 is still initializing or processing callback
  if (loading || isProcessingCallback) {
    return <LoadingPage />;
  }

  // Auto-login instead of showing login page
  if (!isAuthenticated) {
    return <LoadingPage />;
  }

  return <>{children}</>;
}

// Optional Auth Route wrapper - for public pages that can be viewed without login
// Does NOT redirect to login, renders children regardless of auth state
function OptionalAuthRoute({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth();

  // Check if we're processing an Auth0 callback (URL has code and state params)
  const isProcessingCallback = window.location.search.includes('code=') &&
                                window.location.search.includes('state=');

  // If Auth0 is still initializing or processing callback, show loading
  if (loading || isProcessingCallback) {
    return <LoadingPage />;
  }

  // Render children regardless of auth state - no redirect to login
  return <>{children}</>;
}

// Admin-only email list
const ADMIN_EMAILS = ['shim.razilov@gmail.com'];

// Protected Admin Route wrapper - requires admin email
function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, login, user } = useAuth();

  // Check if we're processing an Auth0 callback
  const isProcessingCallback = window.location.search.includes('code=') &&
                                window.location.search.includes('state=');

  // Auto-login effect
  useEffect(() => {
    if (!loading && !isAuthenticated && !isProcessingCallback) {
      const currentPath = window.location.pathname + window.location.search;
      login(currentPath);
    }
  }, [loading, isAuthenticated, login, isProcessingCallback]);

  if (loading || isProcessingCallback) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <LoadingPage />;
  }

  // Check if user is admin
  const userEmail = user?.email?.toLowerCase();
  if (!userEmail || !ADMIN_EMAILS.includes(userEmail)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Main App Content with Router
function AppContent() {
  const { logout } = useAuth();

  // Setup axios interceptor for 401 error handling
  useEffect(() => {
    setupAxiosInterceptor(logout);
  }, [logout]);

  return (
    <Router>
      <Routes>
        {/* All routes wrapped in ProtectedRoute */}
        <Route path="/" element={<ProtectedRoute><Identities /></ProtectedRoute>} />
        <Route path="/identities/:identityId" element={<ProtectedRoute><IdentityDetail /></ProtectedRoute>} />
        <Route path="/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />
        {/* old path — keep bookmarks/links working */}
        <Route path="/accounts" element={<Navigate to="/activity" replace />} />
        <Route path="/runs/:runId" element={<ProtectedRoute><RunDetail /></ProtectedRoute>} />
        <Route path="/api-keys" element={<ProtectedRoute><ApiKeys /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
        <Route path="/organization/:orgId/settings" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
        <Route path="/invite/:token" element={<OptionalAuthRoute><AcceptInvite /></OptionalAuthRoute>} />
        <Route path="/analytics" element={<ProtectedAdminRoute><Analytics /></ProtectedAdminRoute>} />

        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <OnboardingTour />
    </Router>
  );
}

function App() {
  return (
    <Auth0Wrapper>
      <ThemeProvider>
        <OrganizationProvider>
          <AppContent />
        </OrganizationProvider>
      </ThemeProvider>
    </Auth0Wrapper>
  );
}

export default App;
